import { Divider, Fab, Stack } from "@mui/material"
import { ReactElement } from "react"

import AppButton from "../../components/buttons/appButton"
import SplitButton from "../../components/buttons/split/splitButton"
import AppChip from "../../components/dataDisplay/chips/appChip"
import CopyTypography from "../../components/dataDisplay/typography/copyTypography"
import AppTooltip from "../../components/helper/appTooltip"
import RevalidatingSpinner from "../../components/loading/spinners/revalidatingSpinner"
import { AppCardHeader } from "../../components/surfaces/appCard"
import AppPaper from "../../components/surfaces/appPaper"
import DisplayIfElse from "../../components/wrappers/displayIfElse"
import { FAB_POSITION_DISPATCHER } from "../../constants/positioning"
import { useScreenSize } from "../../hooks/useScreenSize"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

const ACTION_DISPLAY_CEILING = 2

export type HeaderAction = {
    text: string
    color:
        | "error"
        | "success"
        | "warning"
        | "info"
        | "inherit"
        | "primary"
        | "secondary"
    endIcon: ReactElement
    onClick: () => void
    disabled?: boolean
    tooltip?: string
}

export type IsAmendable = {
    text: string
    color:
        | "error"
        | "success"
        | "warning"
        | "info"
        | "inherit"
        | "primary"
        | "secondary"
    endIcon: ReactElement
    onClick: () => void
    disabled?: boolean
    tooltip?: string
}

type HeaderBannerProps = {
    title: string
    subtitle: string
    subSubtitle?: string
    actions?: HeaderAction[]
    IsAmendable?: IsAmendable
    Icon?: ReactElement
    redacted?: boolean | undefined
    reSubmitted?: boolean | undefined
    isValidating?: boolean
}
/**
 * Header banner for webpage displaying title, subtitle and (optionally)
 * a series of action buttons.
 * 
 * Take care when laying out FABs. The layout is sensitive to the length
 * of the button
 * 

 * @example
 *  <HeaderBanner
 *      title="KWENE/10866/V1"
 *      subtitle="KW Energie"
 *      actions={[{
 *          text: "Raise Alert",
 *          color: "error",
 *          endIcon: <AddAlertOutlinedIcon />,
 *          onClick: () => {},
 *      }]}
 *  />
 */
const HeaderBanner = ({
    title,
    subtitle,
    subSubtitle,
    actions,
    Icon,
    redacted,
    reSubmitted,
    isValidating,
    IsAmendable,
}: HeaderBannerProps): ReactElement => {
    const { isTabletOrMob } = useScreenSize()
    return (
        <AppPaper
            sx={{
                display: "flex",
                flexDirection: isTabletOrMob ? "column" : "row",
                justifyContent: "space-between",
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 0, md: GRID_SPACING - 1 }}
                alignItems={{ xs: "left", md: "center" }}
                width="100%"
            >
                <AppCardHeader
                    avatar={
                        isTabletOrMob && isValidating ? (
                            <RevalidatingSpinner
                                isValidating={!!isValidating}
                            />
                        ) : (
                            Icon
                        )
                    }
                    titleTypographyProps={{
                        variant: "h6",
                    }}
                    subheaderTypographyProps={{
                        variant: "body1",
                        fontWeight: "normal",
                    }}
                    title={
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={{ xs: 0, md: GRID_SPACING - 1 }}
                            alignItems={{ xs: "left", md: "center" }}
                        >
                            <CopyTypography
                                text={title}
                                typographyProps={{
                                    variant: "h5",
                                    fontWeight: "bold",
                                }}
                            />
                            <Divider orientation="vertical" flexItem />
                            <CopyTypography
                                text={subtitle}
                                typographyProps={{
                                    variant: "h6",
                                    fontWeight: "light",
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={GRID_SPACING - 1}
                                display={{ xs: "none", md: "block" }}
                            >
                                {redacted && (
                                    <AppTooltip title="This applications has been redacted">
                                        <span>
                                            <AppChip
                                                label="Redacted"
                                                color="error"
                                                variant="filled"
                                            />
                                        </span>
                                    </AppTooltip>
                                )}
                                {reSubmitted && (
                                    <AppTooltip title="This application has been resubmitted at least once">
                                        <span>
                                            <AppChip
                                                label="Resubmitted"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </span>
                                    </AppTooltip>
                                )}
                            </Stack>
                        </Stack>
                    }
                    subheader={
                        isTabletOrMob && redacted
                            ? `${subSubtitle} \u2022 Redacted`
                            : subSubtitle
                    }
                />
                {isTabletOrMob && IsAmendable && (
                    <AppTooltip
                        title={IsAmendable?.tooltip ?? ""}
                        key={IsAmendable?.text}
                    >
                        <span
                            style={{
                                width: "50%",
                                padding: "10px 0",
                                alignSelf: "center",
                            }}
                        >
                            <AppButton
                                fullWidth
                                color={IsAmendable?.color}
                                variant="contained"
                                aria-label={IsAmendable?.text}
                                onClick={IsAmendable?.onClick}
                                endIcon={IsAmendable?.endIcon}
                            >
                                {IsAmendable?.text}
                            </AppButton>
                        </span>
                    </AppTooltip>
                )}
            </Stack>
            {!isTabletOrMob && IsAmendable ? (
                <AppTooltip
                    title={IsAmendable?.tooltip ?? ""}
                    key={IsAmendable?.text}
                >
                    <span
                        style={{
                            alignSelf: "center",
                            marginRight: "10px",
                            marginLeft: "10px",
                        }}
                    >
                        <AppButton
                            color={IsAmendable?.color}
                            variant="contained"
                            aria-label={IsAmendable?.text}
                            onClick={IsAmendable?.onClick}
                        >
                            {IsAmendable?.text}
                        </AppButton>
                    </span>
                </AppTooltip>
            ) : null}
            {isTabletOrMob ? (
                (actions || []).map((action: HeaderAction, index: number) => (
                    <AppTooltip title={action.tooltip ?? ""} key={action.text}>
                        <span>
                            <Fab
                                color={action.color}
                                variant="extended"
                                aria-label={action.text}
                                onClick={action.onClick}
                                disabled={!!action.disabled}
                                sx={{
                                    ...FAB_POSITION_DISPATCHER[index + 1],
                                    textTransform: "none",
                                }}
                            >
                                {action.text}
                            </Fab>
                        </span>
                    </AppTooltip>
                ))
            ) : (
                <Stack
                    spacing={GRID_SPACING}
                    direction={{ md: "row" }}
                    alignItems="center"
                >
                    <RevalidatingSpinner isValidating={!!isValidating} />
                    <DisplayIfElse
                        condition={
                            (actions || []).length > ACTION_DISPLAY_CEILING
                        }
                        displayIfTrue={
                            <SplitButton
                                actions={(actions ?? []).map((action) => ({
                                    title: action.text,
                                    onClick: action.onClick,
                                    disabled: action.disabled,
                                    tooltip: action.tooltip,
                                }))}
                            />
                        }
                        displayIfFalse={
                            <>
                                {(actions || []).map(
                                    (action: HeaderAction, index: number) => (
                                        <AppTooltip
                                            title={action.tooltip ?? ""}
                                            key={action.text}
                                        >
                                            <span>
                                                <AppButton
                                                    variant={
                                                        index % 2 === 0
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    color={action.color}
                                                    endIcon={action.endIcon}
                                                    onClick={action.onClick}
                                                    disabled={action.disabled}
                                                >
                                                    {action.text}
                                                </AppButton>
                                            </span>
                                        </AppTooltip>
                                    )
                                )}
                            </>
                        }
                    />
                </Stack>
            )}
        </AppPaper>
    )
}
HeaderBanner.defaultProps = {
    actions: null,
    subSubtitle: null,
    Icon: null,
    redacted: false,
    isValidating: false,
    reSubmitted: false,
    IsAmendable: null,
}
export default HeaderBanner
